import React, { createContext, useEffect, useState, useMemo, useContext } from "react";
import { debounce } from "lodash";

import { initWebMap, initView } from "data/map";
import {
  fetchTrailsInExtent,
  getAllTrailFeaturesByTrailId,
  getTrailFeature,
  filterMapDataForTrailSelected,
  filterMapDataForPoiSelected,
  getPoi,
  getAllNearbyTrails,
  turnOnHighlights,
  isTrailheadId,
  getTrailheadFeatureByObjectId,
  isPoiId,
} from "data/map";
import { webmapId } from "constants/map";

import {
  activityTypeCodes,
  difficultyCodes,
  primaryTypeCodes,
  suitabilityTypeCodes,
} from "components/Filter/PopoverMenu/config";

export const MapContext = createContext();

const MapContextProvider = (props) => {
  const [mapView, setMapView] = useState(null);
  const [showAppLaunchModal, setShowAppLaunchModal] = useState(true);
  const [selection, setSelection] = useState(null);
  const [ready, setReady] = useState(false);
  const [featureList, setFeatureList] = useState([]);
  const [managedAreaFeatures, setManagedAreaFeatures] = useState([]);
  const [managerFeatures, setManagerFeatures] = useState([]);
  const [filteredFeatureList, setFilteredFeatureList] = useState([]);
  const [advancedOptions, setAdvancedOptions] = useState({});
  const [sidebarSelection, setSidebarSelection] = useState(null);
  const [clickedTab, setClickedTab] = useState("Explorer");
  const [extentHistory, setExtentHistory] = useState(null);
  const [featureListHistory, setFeatureListHistory] = useState(null);
  const [returnedHistory, setReturnedHistory] = useState(false);
  const [container, setContainer] = useState(null);
  const [watchHandle, setWatchHandle] = useState(null);

  useMemo(() => {
    initWebMap(webmapId);
  }, []);

  const setCardHistory = () => {
    setFeatureListHistory(filteredFeatureList);
  };

  const resetCardsToPrevious = () => {
    console.log("resetCardsToPrevious", featureListHistory);
    if (featureListHistory) {
      setFilteredFeatureList(featureListHistory);
      setFeatureListHistory(null);
      setReturnedHistory(true);
    }
  };

  const resetCards = () => {
    setFeatureListHistory(null);
    setReturnedHistory(false);
  };

  // call initView when the mapView is ready, and set the selection if its in the route
  useEffect(() => {
    const init = async function () {
      if (container) {
        const view = await initView(container);

        // view.allLayerViews.items.map((lv) => {
        //   console.log(lv);
        //   return lv;
        // });

        setMapView(view);
        setReady(true);
      }
    };
    init();
  }, [container]);

  useEffect(() => {
    const handleMapReady = () => {
      mapView.on("click", (event) => {
        mapView.hitTest(event).then(async (response) => {
          // do something with the result graphic
          if (response.results.length) {
            let graphic = response.results[0].graphic;

            if (
              graphic &&
              graphic.layer &&
              (graphic.layer.geometryType === "polyline" || graphic.attributes.PRIMARY_TRAIL_ID)
            ) {
              //Turn on highlights for trails which were selected previously
              await turnOnHighlights();

              const features = graphic.attributes.PRIMARY_TRAIL_ID
                ? await getAllTrailFeaturesByTrailId(graphic.attributes.PRIMARY_TRAIL_ID, mapView.spatialReference)
                : await getTrailFeature(graphic.attributes.PRIMARY_NAME);

              await filterMapDataForTrailSelected(features);

              if (features && features.length) {
                const trailId = graphic.attributes.PRIMARY_TRAIL_ID
                  ? graphic.attributes.PRIMARY_TRAIL_ID
                  : features[0].attributes?.PRIMARY_TRAIL_ID;

                const trailGeometry =
                  features[0].geometry?.paths?.length > 10 ? features[0].geometry.extent : features[0].geometry;

                const nearbyFeatures = await getAllNearbyTrails(trailGeometry, trailId);

                setSidebarSelection(["Trail", features[0].attributes, nearbyFeatures, features[0]]);
              }
            } else if (isPoiId(graphic.layer.title)) {
              await filterMapDataForPoiSelected(graphic.attributes.OBJECTID, graphic.layer);
              const feature = await getPoi(graphic.attributes.OBJECTID, graphic.layer);
              if (feature && feature.length) {
                setSidebarSelection(["POI", feature[0].attributes]);
              }
            } else if (isTrailheadId(graphic.layer.title)) {
              await filterMapDataForPoiSelected(graphic.attributes.OBJECTID, graphic.layer);
              const res = await getTrailheadFeatureByObjectId(graphic.attributes.OBJECTID);
              if (res && res.features && res.features.length) {
                setSidebarSelection(["Trailhead POI", res.features[0]]);
              }
            } else {
              //Turn on highlights for trails which were selected previously
              await turnOnHighlights();
              mapView.graphics.removeAll();
              setSidebarSelection(null);
            }
          } else {
            //Turn on highlights for trails which were selected previously
            await turnOnHighlights();
            mapView.graphics.removeAll();
            setSidebarSelection(null);
          }
        });
      });
    };

    ready && handleMapReady();
    ready &&
      mapView.when(() => {
        setWatchHandle(mapView.watch("extent", debounce(handleMapExtentChanged, 500)));
      });
  }, [ready]);

  useEffect(() => {
    if (managerFeatures?.length && watchHandle) {
      setFilteredFeatureList(managerFeatures);
      setSidebarSelection(null);
      watchHandle?.remove();
      mapView.goTo(extentHistory).then(() => {
        setManagerFeatures([]);
      });
    } else if (!managerFeatures?.length) {
      watchHandle?.remove();
      watchHandle && setWatchHandle(mapView?.watch("extent", debounce(handleMapExtentChanged, 500)));
    }
  }, [managerFeatures]);

  useEffect(() => {
    if (managedAreaFeatures?.length && watchHandle) {
      setFilteredFeatureList(managedAreaFeatures);
      setSidebarSelection(null);
      watchHandle?.remove();
      mapView.goTo(extentHistory).then(() => {
        setManagedAreaFeatures([]);
      });
    } else if (!managedAreaFeatures?.length) {
      watchHandle?.remove();
      watchHandle && setWatchHandle(mapView?.watch("extent", debounce(handleMapExtentChanged, 500)));
    }
  }, [managedAreaFeatures]);

  useEffect(() => {
    watchHandle?.remove();
    setWatchHandle(mapView?.watch("extent", debounce(handleMapExtentChanged, 500)));
    if (JSON.stringify(advancedOptions) !== "{}") {
      const updateTrailsWithFilters = async () => {
        //Checks if any of teh filters options are True
        const isAnyOptionSelected = Object.entries(advancedOptions).some(([optionType, options]) => {
          if (Object.values(options).some((val) => val)) {
            return true;
          }
        });

        //Only fetches new set of trails within map extent , when any one of teh options have been selected or when no option selected + featureList is empty
        if (
          (!isAnyOptionSelected && !props.searchTerm) ||
          isAnyOptionSelected ||
          (!isAnyOptionSelected && !filteredFeatureList.length)
        ) {
          const trails = await fetchTrailsInExtent(mapView);

          // If trails is undefined, abort
          if (!trails) {
            // TODO: Fix issue where this happens on initial load
            console.log("no trails!", trails);
            return;
          }

          const filteredTrails = trails.filter((trail) => trail.attributes.CONFIGURATION !== "Connectr");
          let filteredFeatureListCopy = filteredTrails;
          Object.entries(advancedOptions).forEach(([optionType, options]) => {
            if (Object.values(options).some((val) => val)) {
              filteredFeatureListCopy = filterListByOptions(
                filteredFeatureListCopy,
                optionType,
                Object.entries(options)
                  .filter(([attribute, val]) => val)
                  .map(([attribute, val]) => attribute)
              );
            }
          });
          setFilteredFeatureList(filteredFeatureListCopy);
        }
      };

      updateTrailsWithFilters();
    }
  }, [advancedOptions]);

  const filterListByOptions = (newFeatureList, attribute, values) => {
    return newFeatureList.filter((feature) => {
      if (attribute === "Difficulty") {
        if (
          values.some((value) => {
            if (feature.attributes.DIFFICULTY === difficultyCodes[value]) return true;
            return false;
          })
        ) {
          return true;
        }
      } else if (attribute === "Length") {
        let lowerLimit = advancedOptions["Length"][0];
        let upperLimit = advancedOptions["Length"][1] >= 20 ? 10000 : advancedOptions["Length"][1];
        if (
          values.some((value) => {
            let length = Math.round(10 * (feature.attributes.Length_SPC / 5280)) / 10;
            if (length > lowerLimit && length < upperLimit) return true;
            return false;
          })
        ) {
          return true;
        }
      } else if (attribute === "Primary Trail Type") {
        if (
          values.some((value) => {
            if (feature.attributes.PRIMARY_TYPE === primaryTypeCodes[value]) return true;
            return false;
          })
        ) {
          return true;
        }
      } else if (attribute === "Activity Type") {
        const allowedVals = ["Yes", "Recmnd", "Allow", "Open"];
        if (
          values.some((value) => {
            if (allowedVals.includes(feature.attributes[activityTypeCodes[value]])) return true;
            return false;
          })
        ) {
          return true;
        }
      } else if (attribute === "Suitability") {
        if (
          values.some((value) => {
            let allowedVals = suitabilityTypeCodes[value]["allowedValues"];
            if (allowedVals.includes(feature.attributes[suitabilityTypeCodes[value]["field"]])) return true;
            return false;
          })
        ) {
          return true;
        }
      }
      return false;
    });
  };

  const handleMapExtentChanged = async (stationary) => {
    // If stationary is false, the map is still being moved, abort
    if (!stationary) return;

    // console.log(
    //   "handleMapExtentChanged",
    //   featureListHistory,
    //   returnedHistory
    // );

    if (featureListHistory?.length > 0) {
      return;
    }
    if (returnedHistory) {
      setReturnedHistory(false);
      return;
    }

    // Since map is now stationary, fetch trails
    if (!managerFeatures.length) {
      const trails = await fetchTrailsInExtent(mapView);

      // If trails is undefined, abort
      if (!trails) {
        // TODO: Fix issue where this happens on initial load
        console.log("no trails!", trails);
        return;
      }

      const filteredTrails = trails.filter((trail) => trail.attributes.CONFIGURATION !== "Connectr");
      setFeatureList(filteredTrails);

      let filteredFeatureListCopy = filteredTrails;

      Object.entries(advancedOptions).forEach(([optionType, options]) => {
        if (Object.values(options).some((val) => val)) {
          filteredFeatureListCopy = filterListByOptions(
            filteredFeatureListCopy,
            optionType,
            Object.entries(options)
              .filter(([attribute, val]) => val)
              .map(([attribute, val]) => attribute)
          );
        }
      });

      setFilteredFeatureList(filteredFeatureListCopy);
    }
  };

  return (
    <MapContext.Provider
      value={{
        mapView,
        setMapView,
        selection,
        setSelection,
        featureList,
        setFeatureList,
        filteredFeatureList,
        setFilteredFeatureList,
        ready,
        advancedOptions,
        setAdvancedOptions,
        sidebarSelection,
        setSidebarSelection,
        managedAreaFeatures,
        setManagedAreaFeatures,
        managerFeatures,
        setManagerFeatures,
        clickedTab,
        setClickedTab,
        extentHistory,
        setExtentHistory,
        resetCardsToPrevious,
        setCardHistory,
        featureListHistory,
        returnedHistory,
        setReturnedHistory,
        resetCards,
        container,
        setContainer,
        setShowAppLaunchModal,
        showAppLaunchModal,
      }}
    >
      {props.children}
    </MapContext.Provider>
  );
};

export default MapContextProvider;
